import { Box, Card } from '@mui/material';
import { useTranslate } from 'react-admin';
import type { TestBenchPaymentSummary } from '@/api/models';
import { payApiClient } from '@/api/client';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { CustomDataGrid, type DataGridColumn } from '@/components/shell/CustomDataGrid';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { StatusChip } from '@/components/shell/StatusChip';
import { useApiResource } from '@/hooks/useApiResource';
import { formatMinorUnits } from '@/utils/amount';
import { isTerminalStatus } from './domain';

interface TestBenchRecentPaymentsProps {
  selectedIntentId: string | undefined;
  onSelect: (intentId: string) => void;
}

/** `GET /v1/admin/test-bench/payments?limit=20` — click a row to drive the live timeline below. */
export function TestBenchRecentPayments({ selectedIntentId, onSelect }: TestBenchRecentPaymentsProps) {
  const translate = useTranslate();
  const { data, loading, error, reload } = useApiResource(() => payApiClient.listTestBenchPayments(20), 'test-bench-recent-payments');

  if (loading) {
    return (
      <Card>
        <LoadingState variant="list" rows={4} />
      </Card>
    );
  }

  if (error) {
    if (error.errorCode === 'access_denied') {
      return (
        <Card>
          <AccessDenied />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.testBench.recent.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Card>
        <EmptyState title={translate('pay.testBench.recent.emptyTitle')} body={translate('pay.testBench.recent.emptyBody')} />
      </Card>
    );
  }

  const columns: Array<DataGridColumn<TestBenchPaymentSummary>> = [
    {
      key: 'orderReference',
      label: translate('pay.testBench.recent.columnOrderReference'),
      mono: 'code',
      render: (item) => item.orderReference,
    },
    {
      key: 'tenant',
      label: translate('pay.testBench.recent.columnTenant'),
      mono: 'code',
      render: (item) => item.tenantCode,
    },
    {
      key: 'amount',
      label: translate('pay.testBench.recent.columnAmount'),
      render: (item) => formatMinorUnits(item.amountMinor, item.currency),
    },
    {
      key: 'status',
      label: translate('pay.testBench.recent.columnStatus'),
      render: (item) => (
        <StatusChip
          label={translate(`pay.operations.status.${item.status}`)}
          tone={isTerminalStatus(item.status) ? (item.status === 'captured' ? 'success' : 'danger') : 'info'}
        />
      ),
    },
    {
      key: 'createdAt',
      label: translate('pay.testBench.recent.columnCreatedAt'),
      render: (item) => new Date(item.createdAt).toLocaleString('es-ES'),
    },
  ];

  return (
    <CustomDataGrid
      columns={columns}
      rows={data.items}
      getRowKey={(item) => item.id}
      onRowClick={(item) => onSelect(item.id)}
      rowAriaLabel={translate('pay.testBench.recent.viewTimelineAction')}
      isRowSelected={(item) => item.id === selectedIntentId}
      header={<Box sx={{ px: 2, pt: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>{translate('pay.testBench.recent.title')}</Box>}
    />
  );
}
